import { useEffect, useState } from "react";
import { BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;

const b64ToBytes = (v) => {
    const pad = "=".repeat((4 - (v.length % 4)) % 4);
    return Uint8Array.from(atob((v + pad).replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
};

async function currentSub() {
    const reg = await navigator.serviceWorker.ready;
    return reg.pushManager.getSubscription();
}

// Staff turn clock-in reminder notifications on/off for this device.
export const ClockInReminderToggle = ({ variant = "dark", className = "", labels = {} }) => {
    const L = { on: "Reminders on", off: "Turn on clock-in reminders", enabled: "Clock-in reminders are on for this device", disabled: "Clock-in reminders turned off", ...labels };
    const [on, setOn] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!supported()) return;
        currentSub().then((s) => setOn(!!s && Notification.permission === "granted")).catch(() => {});
    }, []);

    if (!supported()) {
        if (isIOS() && !standalone()) {
            return <p className={`text-[11px] ${variant === "dark" ? "text-white/50" : "text-ink/50"} ${className}`} data-testid="reminder-ios-hint">Install the app to get clock-in reminders.</p>;
        }
        return null;
    }

    const enable = async () => {
        setBusy(true);
        try {
            const perm = await Notification.requestPermission();
            if (perm !== "granted") throw new Error("Notifications are blocked — allow them in your phone settings");
            const { data } = await api.get("/push/public-key");
            const reg = await navigator.serviceWorker.ready;
            const sub = (await reg.pushManager.getSubscription()) ||
                (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.public_key) }));
            await api.post("/push/subscribe", sub.toJSON());
            await api.post("/push/test").catch(() => {});
            setOn(true);
            toast.success(L.enabled);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message || "Could not turn on reminders");
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        setBusy(true);
        try {
            const sub = await currentSub();
            if (sub) {
                await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
                await sub.unsubscribe();
            }
            setOn(false);
            toast.success(L.disabled);
        } finally {
            setBusy(false);
        }
    };

    const styles = variant === "dark" ? "bg-white/10 text-white hover:bg-white/20" : "border border-ink/15 bg-white text-ink hover:bg-ink/5";
    const Icon = on ? BellRing : BellOff;
    return (
        <button type="button" onClick={on ? disable : enable} disabled={busy} data-testid="clockin-reminder-toggle"
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 ${styles} ${className}`}>
            <Icon className={`w-4 h-4 ${on ? "text-emerald-400" : ""}`} strokeWidth={1.75} />
            {busy ? "Please wait…" : on ? L.on : L.off}
        </button>
    );
};

export default ClockInReminderToggle;
